import { useMutation, useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import {
  GraphQLAPI,
  GraphQLResult,
  queries,
  GraphQLQuery,
  GetWorkspaceUserResult,
} from "@examspark/api";
import { collectAllPages } from "@examspark/common";
import { UpdateWorkspaceInput, WorkspaceUser } from "@examspark/types";

export const useQueryListWorkspaceUsers = (workspaceId?: string) => {
  type TResult = {
    listManagedWorkspaceUsers: {
      items: WorkspaceUser[];
      nextToken?: string;
    };
  };

  return useQuery({
    enabled: !!workspaceId,
    queryKey: ["workspace-users", workspaceId],
    queryFn: async () => {
      const pages = await collectAllPages(async (nextToken) => {
        const page = await GraphQLAPI.query<GraphQLQuery<TResult>>({
          query: /* GraphQL */ `
            query ListManagedWorkspaceUsers($input: ModelListInput!) {
              listManagedWorkspaceUsers(input: $input) {
                items {
                  id
                  sk
                  acl
                  isDisabled
                  tags {
                    key
                    value
                  }
                  user {
                    id
                    sk
                    firstName
                    lastName
                    email
                    avatarHash
                  }
                }
                nextToken
              }
            }
          `,
          variables: {
            input: {
              id: workspaceId,
              nextToken,
            },
          },
        });

        return page.data?.listManagedWorkspaceUsers;
      });

      return pages.flatMap((page) => page.items);
    },
  });
};

export const useMutationGetWorkspaceUser = () => {
  return useMutation({
    mutationFn: async (params: { workspaceId: string; userId: string }) => {
      const result = (await GraphQLAPI.query({
        query: queries.workspace.getWorkspaceUser({
          label: "GetWorkspaceUser",
        }),
        variables: {
          input: {
            id: params.workspaceId,
            sk: `workspace-user#${params.userId}`,
          },
        },
      })) as GraphQLResult<{
        getWorkspace: GetWorkspaceUserResult;
      }>;

      return produce(result.data?.getWorkspace, (draft) => draft);
    },
  });
};

export const useMutationUpdateWorkspaceUser = () => {
  return useMutation({
    mutationFn: async (params: {
      workspaceId: string;
      userId: string;
      data: Omit<UpdateWorkspaceInput, "id" | "sk">;
    }) => {
      return await GraphQLAPI.query({
        query: queries.workspace.updateWorksapceUser,
        variables: {
          input: {
            ...params.data,
            id: params.workspaceId,
            sk: "workspace-user#" + params.userId,
          },
        },
      });
    },
  });
};
